import {css, StyleSheet} from 'aphrodite';
import React, {CSSProperties, useEffect, useRef} from 'react';

import {TABLE_HEADER_HEIGHT} from 'notebook/tables/TableConstants';
import {AlignmentViewRow} from 'search/blast/AlignmentView';
import {AlignmentSelect, setEndSelect, Point} from 'search/blast/BLASTAlignmentSelectHelper';
import {
  ALIGNMENT_TABLE_SCROLLBAR_BUFFER,
  boundMinMax,
  getAdjustedX,
  getAdjustedY,
  getTotalComponentSize,
  PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD,
  roundToNearestMultiple,
  divScrollTo,
  zoomToPairwiseItemSize,
} from 'search/blast/BLASTAlignmentUtil';
import {HORIZONTAL_SCROLLBAR_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';

interface ScrollbarAndSelectHandlerProps {
  scroll: number;
  setScroll: React.Dispatch<React.SetStateAction<number>>;
  // This is when the scrollbar is updated by other actions and not by manually scrolling
  customScroll: number;
  setCustomScroll: React.Dispatch<React.SetStateAction<number>>;
  sequenceWidth: number;
  // This is the sequence column with padding
  scrollableAreaLength: number;
  scrollableAreaHeight: number;
  handleDragStart: (e: React.MouseEvent<HTMLDivElement>) => void;
  dragWindowStart: Point;
  dragWindowEnd: Point;
  setDragWindowEnd: React.Dispatch<React.SetStateAction<Point>>;
  alignmentSelect: AlignmentSelect | null;
  setAlignmentSelect: React.Dispatch<React.SetStateAction<AlignmentSelect | null>>;
  queryRowHeight: number;
  normalRowHeight: number;
  zoom: number;
  tableSize: {width: number; height: number};
  windowSize: {width: number; height: number};
  queryLength: number;
  verticalScroll: number;
  rows: Array<AlignmentViewRow>;
}
export default function ScrollbarAndSelectHandler(props: ScrollbarAndSelectHandlerProps): JSX.Element {
  // need to adjust the dimensions depending on the actual scroll window buffer since
  // the actual column width is SEQUENCE_COLUMN_PADDING px larger than its contents
  const scale = props.scrollableAreaLength / props.sequenceWidth;
  const scaledInnerWidth = getTotalComponentSize(props.zoom, props.sequenceWidth, props.queryLength) * scale;

  const outsideStyle: CSSProperties = {
    width: `${props.scrollableAreaLength}px`,
    height: `${HORIZONTAL_SCROLLBAR_HEIGHT}px`,
  };
  const innerStyle: CSSProperties = {
    width: `${scaledInnerWidth}px`,
  };

  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    divScrollTo(divRef, props.customScroll * scale);
  }, [props.customScroll, scale]);

  const tableHeight = props.tableSize.height;
  const barHeight = tableHeight - TABLE_HEADER_HEIGHT;

  useEffect(
    () => {
      // triggers when the user clicks
      if (props.dragWindowStart.x >= 0) {
        attachDragListener();
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [props.dragWindowStart]
  );

  /*
   * In order to have natural scrolling in both directions, when there's vertical scrolling, this shrinks
   * the fake scrollbar div to 16px so that the underlying table scrolling behavior can work.
   * When there's horizontal scrolling, it expands up to the height of the table, so that you can horizontally
   * scroll with your mouse hovered over anywhere in the alignment column.
   *
   * It default returns to 16px after some delay so that any hover behavior can still work after the
   * scrolling is done.
   */
  useEffect(() => {
    const changeScrollbarHeight = (e: WheelEvent): void => {
      if (divRef.current == null) {
        return;
      }

      if (
        Math.abs(e.deltaX) > Math.abs(e.deltaY) &&
        divRef.current.style.height === `${HORIZONTAL_SCROLLBAR_HEIGHT}px`
      ) {
        divRef.current.style.height = `${props.scrollableAreaHeight}px`;
        e.preventDefault();

        // reset to low height so that the hovering over alignments will still work
        setTimeout(() => {
          if (divRef.current) {
            divRef.current.style.height = `${HORIZONTAL_SCROLLBAR_HEIGHT}px`;
          }
        }, 1000);
      } else if (
        Math.abs(e.deltaY) > Math.abs(e.deltaX) &&
        divRef.current.style.height !== `${HORIZONTAL_SCROLLBAR_HEIGHT}px`
      ) {
        divRef.current.style.height = `${HORIZONTAL_SCROLLBAR_HEIGHT}px`;
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', changeScrollbarHeight, {passive: false});

    return () => {
      document.removeEventListener('wheel', changeScrollbarHeight);
    };
  }, [props.scrollableAreaHeight]);

  useEffect(
    () => {
      // this allows handling scrolling while dragging
      if (dragging) {
        const totalComponentSize = getTotalComponentSize(props.zoom, props.sequenceWidth, props.queryLength);
        const pixelEndX = props.dragWindowEnd.x + props.scroll;
        const residueEndX = (pixelEndX / totalComponentSize) * props.queryLength;
        const pixelEndY = props.dragWindowEnd.y + props.verticalScroll;

        setEndSelect(
          props.setAlignmentSelect,
          residueEndX,
          pixelEndY,
          props.dragWindowStart.y,
          props.rows,
          props.normalRowHeight,
          props.queryRowHeight
        );
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [props.scroll, props.dragWindowEnd]
  );

  const [dragging, setDragging] = React.useState(false);

  const handleDragMove = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);

    const adjX = boundMinMax(
      0,
      getAdjustedX(e.pageX, props.windowSize.width, props.scrollableAreaLength),
      props.sequenceWidth
    );
    const adjY = boundMinMax(
      0,
      getAdjustedY(e.pageY, props.windowSize.height, props.tableSize.height),
      barHeight
    );

    if (props.zoom >= PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
      const roundedNewTranslateX = roundToNearestMultiple(adjX, zoomToPairwiseItemSize(props.zoom));
      props.setDragWindowEnd({x: roundedNewTranslateX, y: adjY});
    } else {
      props.setDragWindowEnd({x: adjX, y: adjY});
    }
  };

  const handleDragEnd = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    detachDragListener();
  };

  const attachDragListener = (): void => {
    setDragging(true);
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
  };
  const detachDragListener = (): void => {
    setDragging(false);
    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
  };

  return (
    <div
      className={css(styles.outside)}
      style={outsideStyle}
      onScroll={(event: React.UIEvent<HTMLElement>) => {
        const {scrollLeft} = event.target as HTMLElement;
        // since the scrollbar is slightly bigger than it should be, we need to scale down
        const adjustedScroll = scrollLeft / scale;
        if (Math.abs(adjustedScroll - props.scroll) > 1) {
          props.setScroll(adjustedScroll);
        }
      }}
      ref={divRef}
      onMouseDown={props.handleDragStart}
    >
      <div style={innerStyle} />
    </div>
  );
}

export const styles = StyleSheet.create({
  outside: {
    zIndex: 99,
    overflowX: 'scroll',
    position: 'absolute',
    right: `${ALIGNMENT_TABLE_SCROLLBAR_BUFFER}px`,
    bottom: '0px',
  },
});
